import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TripMapView,
  parseGeoPoint,
  geocodeAddress,
  haversineKm,
  formatKm,
  supabase,
  resolveTripMapPathDisplay,
  locationLogsToTrack,
  type LatLng,
  type LocationLog,
  type MapMarker,
  type TripMapViewHandle,
} from '@ubus/shared';
import SOSButton from './SOSButton';

interface TaskDetailMapProps {
  tripId: string;
  phase: 'not_today' | 'not_yet' | 'ready' | 'in_progress' | 'readonly';
  pickupGeopoint?: string | null;
  dropoffGeopoint?: string | null;
  pickupAddress?: string;
  dropoffAddress?: string;
}

const DEFAULT_CENTER: LatLng = { lat: -33.8688, lng: 151.2093 };

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), ms);
    }),
  ]);
}

function TaskDetailMap({
  tripId,
  phase,
  pickupGeopoint,
  dropoffGeopoint,
  pickupAddress,
  dropoffAddress,
}: TaskDetailMapProps) {
  const { t } = useTranslation();
  const mapRef = useRef<TripMapViewHandle>(null);
  const [distanceLabel, setDistanceLabel] = useState<string | null>(null);
  const [mapError, setMapError] = useState(false);
  const [geocodedPickup, setGeocodedPickup] = useState<LatLng | null>(null);
  const [geocodedDropoff, setGeocodedDropoff] = useState<LatLng | null>(null);
  const [locationLogs, setLocationLogs] = useState<LocationLog[]>([]);

  const pickup = useMemo(() => {
    const fromDb = pickupGeopoint ? parseGeoPoint(pickupGeopoint) : null;
    return fromDb ?? geocodedPickup;
  }, [pickupGeopoint, geocodedPickup]);

  const dropoff = useMemo(() => {
    const fromDb = dropoffGeopoint ? parseGeoPoint(dropoffGeopoint) : null;
    return fromDb ?? geocodedDropoff;
  }, [dropoffGeopoint, geocodedDropoff]);

  const center = pickup ?? dropoff ?? DEFAULT_CENTER;

  const trackPoints = useMemo(() => locationLogsToTrack(locationLogs), [locationLogs]);

  const pathDisplay = useMemo(
    () => resolveTripMapPathDisplay(trackPoints, pickup, dropoff),
    [trackPoints, pickup, dropoff]
  );

  const markers: MapMarker[] = useMemo(() => {
    const list: MapMarker[] = [];
    if (pickup) {
      list.push({
        id: 'pickup',
        position: pickup,
        title: t('task.pickupMarker'),
        label: 'P',
        role: 'pickup',
      });
    }
    if (dropoff) {
      list.push({
        id: 'dropoff',
        position: dropoff,
        title: t('task.dropoffMarker'),
        label: 'D',
        role: 'dropoff',
      });
    }
    return list;
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, t]);

  useEffect(() => {
    if (phase !== 'in_progress' && phase !== 'readonly') return;
    let cancelled = false;

    (async () => {
      const { data } = await supabase
        .from('location_logs')
        .select('*')
        .eq('trip_id', tripId)
        .order('timestamp', { ascending: true });

      if (!cancelled) setLocationLogs((data ?? []) as LocationLog[]);
    })();

    return () => {
      cancelled = true;
    };
  }, [tripId, phase]);

  useEffect(() => {
    let cancelled = false;

    async function resolveMissingCoords() {
      if (!pickupGeopoint && pickupAddress?.trim() && !geocodedPickup) {
        const result = await geocodeAddress(pickupAddress.trim());
        if (!cancelled && result) {
          setGeocodedPickup({ lat: result.lat, lng: result.lng });
        }
      }
      if (!dropoffGeopoint && dropoffAddress?.trim() && !geocodedDropoff) {
        const result = await geocodeAddress(dropoffAddress.trim());
        if (!cancelled && result) {
          setGeocodedDropoff({ lat: result.lat, lng: result.lng });
        }
      }
    }

    resolveMissingCoords();
    return () => {
      cancelled = true;
    };
  }, [
    pickupGeopoint,
    dropoffGeopoint,
    pickupAddress,
    dropoffAddress,
    geocodedPickup,
    geocodedDropoff,
  ]);

  const updateDistance = useCallback(async () => {
    const prefixKey =
      phase === 'in_progress' ? 'task.destinationDistance' : 'task.distanceFromStart';

    if (!pickup || !dropoff) {
      setDistanceLabel(null);
      return;
    }

    const straightKm = haversineKm(pickup, dropoff);
    setDistanceLabel(t(prefixKey, { distance: formatKm(straightKm) }));

    const handle = mapRef.current;
    if (!handle) return;

    const routeResult = await withTimeout(handle.getDrivingRoute(pickup, dropoff), 8000);
    if (routeResult && routeResult.distanceMeters > 0) {
      setDistanceLabel(t(prefixKey, { distance: formatKm(routeResult.distanceMeters / 1000) }));
    }
  }, [phase, pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, t]);

  useEffect(() => {
    if (!mapError) {
      updateDistance();
    }
  }, [mapError, updateDistance]);

  const handleLocate = () => {
    if (pickup) mapRef.current?.panTo(pickup);
  };

  const handleRefresh = () => {
    updateDistance();
  };

  if (mapError) {
    return (
      <div className="driver-detail-map driver-detail-map--fallback">
        <div className="driver-detail-map-fallback">
          <p>{pickupAddress || t('task.pickupUnavailable')}</p>
          <p className="driver-detail-map-fallback-arrow">↓</p>
          <p>{dropoffAddress || t('task.dropoffUnavailable')}</p>
        </div>
        {distanceLabel && (
          <div className="driver-detail-map-tooltip driver-detail-map-tooltip--blue">{distanceLabel}</div>
        )}
        <SOSButton tripId={tripId} variant="map" />
      </div>
    );
  }

  return (
    <div className="driver-detail-map">
      <TripMapView
        ref={mapRef}
        center={center}
        zoom={13}
        markers={markers}
        track={pathDisplay.track}
        route={pathDisplay.route}
        className="driver-detail-map-canvas"
        onReady={updateDistance}
        onError={() => setMapError(true)}
      />
      <div className="driver-detail-map-controls">
        <button
          type="button"
          className="driver-detail-map-control"
          aria-label={t('task.refreshRoute')}
          onClick={handleRefresh}
        >
          ↻
        </button>
        <button
          type="button"
          className="driver-detail-map-control"
          aria-label={t('task.centerMap')}
          onClick={handleLocate}
        >
          ⊕
        </button>
      </div>
      {distanceLabel && (
        <div className="driver-detail-map-tooltip driver-detail-map-tooltip--blue">{distanceLabel}</div>
      )}
      <SOSButton tripId={tripId} variant="map" />
    </div>
  );
}

export default TaskDetailMap;
